// Single source of truth for order pricing, shared by the React order form
// (bundled by react-scripts/webpack) and the create-checkout-session Netlify
// function (CommonJS). Keeping one module guarantees the customer-facing total
// and the Stripe charge are computed from identical rules and data.
//
// NOTE: pricing VALUES here are the approved production prices — do not change
// them without explicit approval.

// ── ZIP CODE DATA ──────────────────────────────────────────────────────────────
const ZIP_DATA = {
  // BASE ZONE – Corona & Norco (flat $85)
  "92878": { city: "Corona", zone: "base", miles: 0 },
  "92879": { city: "Corona", zone: "base", miles: 0 },
  "92880": { city: "Corona", zone: "base", miles: 0 },
  "92881": { city: "Corona", zone: "base", miles: 0 },
  "92882": { city: "Corona", zone: "base", miles: 0 },
  "92883": { city: "Corona", zone: "base", miles: 0 },
  "92860": { city: "Norco", zone: "base", miles: 0 },

  // EXTENDED – Eastvale
  "91752": { city: "Eastvale", zone: "extended", miles: 10 },

  // EXTENDED – Rancho Cucamonga
  "91701": { city: "Rancho Cucamonga", zone: "extended", miles: 22 },
  "91730": { city: "Rancho Cucamonga", zone: "extended", miles: 22 },
  "91737": { city: "Rancho Cucamonga", zone: "extended", miles: 22 },
  "91739": { city: "Rancho Cucamonga", zone: "extended", miles: 22 },

  // EXTENDED – Ontario
  "91710": { city: "Ontario", zone: "extended", miles: 18 },
  "91761": { city: "Ontario", zone: "extended", miles: 18 },
  "91762": { city: "Ontario", zone: "extended", miles: 18 },
  "91764": { city: "Ontario", zone: "extended", miles: 18 },

  // EXTENDED – Jurupa Valley
  "92509": { city: "Jurupa Valley", zone: "extended", miles: 8 },

  // EXTENDED – Riverside (by zip – varies)
  "92501": { city: "Riverside (Downtown)", zone: "extended", miles: 14 },
  "92502": { city: "Riverside", zone: "extended", miles: 14 },
  "92503": { city: "Riverside (Arlington)", zone: "extended", miles: 12 },
  "92504": { city: "Riverside (Magnolia)", zone: "extended", miles: 12 },
  "92505": { city: "Riverside (La Sierra)", zone: "extended", miles: 10 },
  "92506": { city: "Riverside (Canyon Crest)", zone: "extended", miles: 18 },
  "92507": { city: "Riverside (University)", zone: "extended", miles: 16 },
  "92508": { city: "Riverside (Orangecrest)", zone: "extended", miles: 20 },
  "92521": { city: "Riverside (UCR Area)", zone: "extended", miles: 16 },

  // EXTENDED – Lake Matthews
  "92570": { city: "Lake Mathews", zone: "extended", miles: 16 },

  // EXTENDED – Anaheim Hills
  "92807": { city: "Anaheim Hills", zone: "extended", miles: 25 },
  "92808": { city: "Anaheim Hills", zone: "extended", miles: 25 },

  // EXTENDED – Yorba Linda
  "92886": { city: "Yorba Linda", zone: "extended", miles: 28 },
  "92887": { city: "Yorba Linda", zone: "extended", miles: 28 },

  // EXTENDED – Placentia
  "92870": { city: "Placentia", zone: "extended", miles: 30 },

  // EXTENDED – Orange
  "92856": { city: "Orange", zone: "extended", miles: 32 },
  "92857": { city: "Orange", zone: "extended", miles: 32 },
  "92859": { city: "Orange", zone: "extended", miles: 32 },
  "92861": { city: "Orange", zone: "extended", miles: 32 },
  "92862": { city: "Orange", zone: "extended", miles: 32 },
  "92863": { city: "Orange", zone: "extended", miles: 32 },
  "92864": { city: "Orange", zone: "extended", miles: 32 },
  "92865": { city: "Orange", zone: "extended", miles: 32 },
  "92866": { city: "Orange", zone: "extended", miles: 32 },
  "92867": { city: "Orange", zone: "extended", miles: 32 },
  "92868": { city: "Orange", zone: "extended", miles: 32 },
  "92869": { city: "Orange", zone: "extended", miles: 32 },

  // EXTENDED – Lake Elsinore
  "92530": { city: "Lake Elsinore", zone: "extended", miles: 22 },
  "92531": { city: "Lake Elsinore", zone: "extended", miles: 22 },
  "92532": { city: "Lake Elsinore", zone: "extended", miles: 22 },

  // EXTENDED – Moreno Valley
  "92551": { city: "Moreno Valley", zone: "extended", miles: 24 },
  "92552": { city: "Moreno Valley", zone: "extended", miles: 24 },
  "92553": { city: "Moreno Valley", zone: "extended", miles: 24 },
  "92554": { city: "Moreno Valley", zone: "extended", miles: 24 },
  "92555": { city: "Moreno Valley", zone: "extended", miles: 24 },
  "92556": { city: "Moreno Valley", zone: "extended", miles: 24 },
  "92557": { city: "Moreno Valley", zone: "extended", miles: 24 },
};

const PRICING = {
  standard: { label: "Standard",  desc: "48-hr attempt intervals",   price: 100 },
  rush:     { label: "Rush",      desc: "24-hr attempt intervals", updates: "Live updates at each attempt", price: 150 },
  sameday:  { label: "Same-Day",  desc: "Attempted within hours", updates: "Live updates at each attempt", price: 175 },
};

// Single source of truth for the customer-facing business phone number. Shown in
// the Same-Day Service calculator so attorneys can call to confirm availability
// before ordering. _DISPLAY is what users see; _TEL is the E.164 form used for
// tap-to-call `tel:` links.
const BUSINESS_PHONE_DISPLAY = "(951) 254-2382";
const BUSINESS_PHONE_TEL = "+19512542382";
const DEFAULT_BASE_PRICE = 100; // fallback when a tier is unknown
const MILEAGE_RATE = 2; // per mile each way

// LARGE PACKET: every page over 50 is $0.50. Pages are counted server-side
// from the uploaded files; the first 50 pages are included in every price.
const LARGE_PACKET = { freePages: 50, centsPerPage: 50 };
// Fee in whole dollars (cents-exact) for a packet of `totalPages`.
function packetPageFee(totalPages) {
  const n = Math.floor(Number(totalPages));
  if (!Number.isFinite(n) || n <= LARGE_PACKET.freePages) return 0;
  return ((n - LARGE_PACKET.freePages) * LARGE_PACKET.centsPerPage) / 100;
}

// Optional add-ons, ONE per order (orders.service_addon is a single value).
// Each add-on names the service levels it may be used with:
//   - Weekend / Holiday: Rush or Same-Day only.
//   - Priority: Standard only — it buys the live attempt updates that Rush and
//     Same-Day already include, so it is never offered (or charged) on those.
// Because their service levels never overlap, Priority never competes with
// Weekend/Holiday for the single add-on slot.
const ADDONS = {
  weekend:  { label: "Weekend Service", price: 50,  tiers: ["rush", "sameday"] },
  holiday:  { label: "Holiday Service", price: 100, tiers: ["rush", "sameday"] },
  priority: { label: "Priority", price: 75, tiers: ["standard"], desc: "Live updates at each attempt" },
};
// Service levels that may carry at least one add-on.
const ADDON_ELIGIBLE_TIERS = ["standard", "rush", "sameday"];

// Service levels whose orders get an email update after every attempt, and the
// add-on that buys the same thing on Standard.
const LIVE_UPDATE_TIERS = ["rush", "sameday"];
const LIVE_UPDATE_ADDON = "priority";

const DOC_SURCHARGES = {
  "Summons & Complaint":        { surcharge: 0,   note: "" },
  "Small Claims":               { surcharge: 0,   note: "" },
  "Subpoena":                   { surcharge: 25,  note: "+$25 for subpoena handling" },
  "Unlawful Detainer / Eviction": { surcharge: 75, note: "Additional document surcharge: $75 — 5-day legal deadline" },
  "TRO / Restraining Order":    { surcharge: 75,  note: "Additional document surcharge: $75" },
  "Family Law (Divorce/Custody)": { surcharge: 25, note: "+$25 for family law documents" },
  "Wage Garnishment / Levy":    { surcharge: 0,   note: "" },
  // "Other" carries NO automatic price. The documents have to be read before a
  // price exists, so reviewRequired is authoritative: orderTotal() returns null,
  // the calculator shows no figure, and create-checkout-session refuses to make
  // a Stripe session. The surcharge is irrelevant and is never applied.
  "Other":                      { surcharge: 0,   note: "Pricing requires document review.", reviewRequired: true },
};

// Customer-facing copy shown wherever a total would otherwise appear.
const REVIEW_REQUIRED_MESSAGE = "Pricing requires document review.";

// ── EX PARTE: A HEARING STATUS, NOT A DOCUMENT TYPE ────────────────────────
// Ex parte describes the URGENCY of the hearing the papers relate to. The
// documents themselves are still a family-law petition, a TRO, a subpoena and
// so on, and that is what decides the price and the Proof form.
//
// It was briefly modelled as its own document type. That was wrong: it replaced
// the real document type, so an ex parte family-law order no longer recorded
// that it was family law — losing the information that drives form selection.
// It is now a separate flag alongside the document type.
//
// Its ONLY pricing effect is to restrict the service level. There is no ex
// parte surcharge: an ex parte family-law order costs exactly what the same
// family-law order costs.
const EX_PARTE_ALLOWED_TIERS = ["rush", "sameday"];
const EX_PARTE_TIER_MESSAGE = "Ex Parte service requires Rush or Same-Day service.";

// Documents whose legal deadlines require Rush or Same-Day (published in the
// website FAQ): unlawful detainer / eviction and TROs. Standard is refused for
// them exactly like ex parte — never silently upgraded.
const RUSH_REQUIRED_DOC_TYPES = ["Unlawful Detainer / Eviction", "TRO / Restraining Order"];
function docTypeRequiresRush(docType) {
  return RUSH_REQUIRED_DOC_TYPES.includes(String(docType || "").trim());
}

// A checkbox, a "yes", a string from a form post — all mean the same thing.
// Anything else is false: ex parte is never inferred.
function normalizeExParte(value) {
  if (value === true) return true;
  const v = String(value == null ? "" : value).trim().toLowerCase();
  return v === "true" || v === "yes" || v === "1";
}

// The service levels available, or null when there is no restriction.
function allowedTiersForExParte(exParte) {
  return normalizeExParte(exParte) ? EX_PARTE_ALLOWED_TIERS.slice() : null;
}

// May this service level be used? An ex parte hearing is heard before Standard's
// 48-hour attempt intervals could reach the party, so Standard is refused —
// never substituted. Quietly upgrading to Rush would charge more than the price
// shown; quietly accepting Standard would miss the hearing.
function tierAllowedForExParte(tier, exParte, docType) {
  if (!normalizeExParte(exParte) && !docTypeRequiresRush(docType)) return true;
  return EX_PARTE_ALLOWED_TIERS.includes(String(tier || "").trim());
}

// Why the combination is refused, or "" when it is fine. This exact wording is
// what the customer sees and what the server returns.
function tierRestrictionMessage(tier, exParte, docType) {
  if (tierAllowedForExParte(tier, exParte, docType)) return "";
  if (normalizeExParte(exParte)) return EX_PARTE_TIER_MESSAGE;
  return `${String(docType).trim()} requires Rush or Same-Day service.`;
}

// Whether a document type must be priced by a human after reading the documents.
// A BLANK/unknown document type also requires review — it can never be auto-priced
// or sent to checkout. This is the one rule the order form, the embedded
// calculator and the server all consult.
function requiresReview(docType) {
  const key = String(docType == null ? "" : docType).trim();
  if (!key) return true;                       // blank is never auto-priced
  const entry = DOC_SURCHARGES[key];
  if (!entry) return true;                     // unknown type is never auto-priced
  return entry.reviewRequired === true;
}

// Service fee for a ZIP-data object (base flat, or base + round-trip mileage).
function calcPrice(zipData, tier = "standard") {
  if (!zipData) return null;
  const base = (PRICING[tier] && PRICING[tier].price) || DEFAULT_BASE_PRICE;
  if (zipData.zone === "base") return base;
  const mileageFee = zipData.miles * MILEAGE_RATE * 2;
  return base + mileageFee;
}

// Add-ons offered for a service level, as [key, addon] pairs (UI order).
function addonsForTier(tier) {
  const t = String(tier);
  // No destructuring here: this file is CommonJS, and a Babel helper import for
  // array destructuring would turn it into an ES module in the production build,
  // hiding every module.exports name from App.jsx ("Attempted import error").
  return Object.keys(ADDONS)
    .filter(function (k) { return ADDONS[k].tiers.indexOf(t) !== -1; })
    .map(function (k) { return [k, ADDONS[k]]; });
}

// Without a key: whether the service level has ANY add-on to offer.
// With a key: whether THAT add-on may be used on this service level.
function addonAllowedForTier(tier, key) {
  if (key != null) return !!(ADDONS[String(key)] && ADDONS[String(key)].tiers.includes(String(tier)));
  return addonsForTier(tier).length > 0;
}

// Normalize an add-on to a known key or null. Anything invalid, or an add-on on
// a service level it does not belong to, normalizes to null. This is the single
// rule the UI and the server both apply — the browser is never trusted.
function normalizeAddon(addon, tier) {
  if (!addon) return null;
  const key = String(addon);
  if (!ADDONS[key]) return null;
  if (!addonAllowedForTier(tier, key)) return null;
  return key;
}

// Whether an order gets an email update after each attempt: Rush / Same-Day
// include it; Standard gets it only with the Priority add-on.
function hasLiveUpdates(tier, addon) {
  const t = String(tier || "");
  if (LIVE_UPDATE_TIERS.includes(t)) return true;
  return normalizeAddon(addon, t) === LIVE_UPDATE_ADDON;
}

// Price of a valid add-on for this tier (0 when none / not allowed / invalid).
function addonPrice(addon, tier) {
  const key = normalizeAddon(addon, tier);
  return key ? ADDONS[key].price : 0;
}

// Service fee looked up by ZIP string. Returns null for an unserviceable ZIP.
function servicePriceByZip(zip, tier = "standard") {
  return calcPrice(ZIP_DATA[zip], tier);
}

// Document-type handling surcharge (0 when none / unknown type).
function docSurcharge(docType) {
  return (DOC_SURCHARGES[docType] && DOC_SURCHARGES[docType].surcharge) || 0;
}

// Authoritative order total in whole dollars:
//   service fee (base + mileage) + document surcharge + optional add-on.
// Returns null when the ZIP is not serviceable (no price can be determined).
// An invalid add-on, or any add-on on Standard, contributes $0 (see normalizeAddon).
// Returns null when NO price can be determined — an unserviceable ZIP, or a
// review-required/blank document type. A null total is the signal to every
// caller that there is nothing to display and nothing to charge.
// `exParte` restricts the service level only; it never changes the price. An
// existing 4-argument call means "not ex parte" and is unaffected.
function orderTotal(zip, tier = "standard", docType = "", addon = null, exParte = false) {
  if (requiresReview(docType)) return null;
  // A service level this order may not use has NO price. There is nothing to
  // display and nothing to charge until the customer picks an allowed level —
  // the combination is never silently changed to a valid one.
  if (!tierAllowedForExParte(tier, exParte)) return null;
  const service = servicePriceByZip(zip, tier);
  if (service == null) return null;
  return service + docSurcharge(docType) + addonPrice(addon, tier);
}

// TIER UPGRADE on an order that is already paid (e.g. Standard -> Rush).
// Mileage, document surcharge and page fee do not depend on the service level,
// so the extra charge is the difference in base price. An add-on the new level
// does not allow (Priority on Rush/Same-Day — its live updates are included) is
// dropped and its price credited. Returns { ok, diff (whole dollars), addon }.
const TIER_RANK = { standard: 1, rush: 2, sameday: 3 };
function tierUpgradeQuote(fromTier, toTier, addon) {
  const from = String(fromTier || "standard");
  const to = String(toTier || "");
  if (!TIER_RANK[from] || !TIER_RANK[to] || !PRICING[from] || !PRICING[to]) return { ok: false, reason: "unknown_tier" };
  if (TIER_RANK[to] <= TIER_RANK[from]) return { ok: false, reason: "not_an_upgrade" };
  const a = addon && ADDONS[addon] ? String(addon) : null;
  const keep = a && addonAllowedForTier(to, a) ? a : null;
  const credit = a && !keep && addonAllowedForTier(from, a) ? ADDONS[a].price : 0;
  const diff = PRICING[to].price - PRICING[from].price - credit;
  if (!(diff > 0)) return { ok: false, reason: "no_charge" };
  return { ok: true, diff: diff, addon: keep };
}
function upgradeTargets(fromTier) {
  const r = TIER_RANK[String(fromTier || "standard")] || 0;
  return Object.keys(TIER_RANK).filter(function (t) { return TIER_RANK[t] > r; });
}

module.exports = {
  tierUpgradeQuote,
  upgradeTargets,
  ZIP_DATA,
  PRICING,
  DEFAULT_BASE_PRICE,
  MILEAGE_RATE,
  ADDONS,
  ADDON_ELIGIBLE_TIERS,
  DOC_SURCHARGES,
  REVIEW_REQUIRED_MESSAGE,
  EX_PARTE_ALLOWED_TIERS,
  EX_PARTE_TIER_MESSAGE,
  RUSH_REQUIRED_DOC_TYPES,
  LARGE_PACKET,
  packetPageFee,
  docTypeRequiresRush,
  normalizeExParte,
  allowedTiersForExParte,
  tierAllowedForExParte,
  tierRestrictionMessage,
  requiresReview,
  calcPrice,
  addonAllowedForTier,
  addonsForTier,
  LIVE_UPDATE_TIERS,
  LIVE_UPDATE_ADDON,
  hasLiveUpdates,
  normalizeAddon,
  addonPrice,
  servicePriceByZip,
  docSurcharge,
  orderTotal,
  BUSINESS_PHONE_DISPLAY,
  BUSINESS_PHONE_TEL,
};
